import React, { useEffect, useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { api } from "../api";
import QcChart from "../charts/QcChart";
import { ErrorBanner, Pill, Spinner } from "../components/common";
import { CONDITION_ORDER } from "../lib/conditions";
import { dateTime, fraction, sig } from "../lib/format";
import { getUser } from "../lib/user";

/**
 * Step 2. The internal-standard trace per condition, plus every flag raised.
 *
 * QC never removes anything. It puts the run's system-response stability in
 * front of a scientist, who acknowledges what they have seen before the curves
 * are worked on.
 */
export default function QcScreen() {
  const { runId } = useParams();
  const navigate = useNavigate();
  const { refreshRun } = useOutletContext();
  const [qc, setQc] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    api
      .getQc(runId)
      .then((data) => live && setQc(data))
      .catch((e) => live && setError(e));
    return () => {
      live = false;
    };
  }, [runId]);

  async function acknowledge() {
    setBusy(true);
    setError(null);
    try {
      await api.acknowledgeQc(runId, getUser() || null);
      await refreshRun();
      navigate(`/runs/${runId}/curate`);
    } catch (e) {
      setError(e);
      setBusy(false);
    }
  }

  if (error && !qc) return <ErrorBanner error={error} />;
  if (!qc) return <Spinner label="Running QC…" />;

  const { summary } = qc;
  const traces = [...qc.traces].sort(
    (a, b) => CONDITION_ORDER.indexOf(a.condition) - CONDITION_ORDER.indexOf(b.condition)
  );

  return (
    <>
      <ErrorBanner error={error} onDismiss={() => setError(null)} />

      {summary.n_flags === 0 ? (
        <div className="banner ok">
          <strong>No QC flags.</strong> Internal standard, calibration, controls, carry-over and
          completeness are all within their acceptance limits.
        </div>
      ) : (
        <div className={`banner ${summary.has_errors ? "error" : "warning"}`}>
          <strong>
            {summary.n_errors} error{summary.n_errors === 1 ? "" : "s"}, {summary.n_warnings}{" "}
            warning{summary.n_warnings === 1 ? "" : "s"}.
          </strong>{" "}
          Nothing has been removed from the data. Read the flags, then acknowledge them to
          continue.
        </div>
      )}

      <div className="card">
        <h2>Internal standard across the run</h2>
        <p className="card-note">
          13C6-1-OH-midazolam peak area against injection order. The dashed line is the run
          median and the shaded band the acceptance tolerance; a drift or a spike here means the
          calculated concentrations either side of it are not comparable.
        </p>
        {traces.map((trace) => (
          <QcChart
            key={trace.condition}
            trace={trace}
            flaggedInjections={qc.flags
              .filter((f) => f.condition === trace.condition && f.injection_index)
              .map((f) => f.injection_index)}
          />
        ))}
      </div>

      <div className="split">
        <div className="card">
          <h2>Calibration</h2>
          <table>
            <thead>
              <tr>
                <th>Condition</th>
                <th>Fit</th>
                <th className="num">Intercept</th>
                <th className="num">Slope</th>
                <th className="num">r</th>
              </tr>
            </thead>
            <tbody>
              {traces.map(({ condition }) => {
                const cal = qc.calibrations[condition] || {};
                return (
                  <tr key={condition}>
                    <td>{condition}</td>
                    <td className="muted">
                      {cal.fit_type || "—"}
                      {cal.weighting ? `, ${cal.weighting}` : ""}
                    </td>
                    <td className="num">{sig(cal.intercept, 3)}</td>
                    <td className="num">{sig(cal.slope, 3)}</td>
                    <td className="num">{sig(cal.correlation_coefficient, 5)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="card">
          <h2>Uninhibited controls</h2>
          <p className="card-note">
            The mean of the six Nullwert injections defines 100 % activity for its condition.
            Every percentage in that condition is scaled by it.
          </p>
          <table>
            <thead>
              <tr>
                <th>Condition</th>
                <th className="num unit">Mean [ng/mL]</th>
                <th className="num">CV</th>
              </tr>
            </thead>
            <tbody>
              {traces.map(({ condition }) => (
                <tr key={condition}>
                  <td>{condition}</td>
                  <td className="num">{sig(qc.control_means[condition], 4)}</td>
                  <td className="num">{fraction(qc.control_cvs[condition])}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {qc.flags.length > 0 && (
        <div className="card">
          <h2>Flags</h2>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Severity</th>
                  <th>Category</th>
                  <th>Condition</th>
                  <th>Subject</th>
                  <th style={{ whiteSpace: "normal" }}>Message</th>
                </tr>
              </thead>
              <tbody>
                {qc.flags.map((flag, i) => (
                  <tr key={`${flag.category}-${flag.subject}-${i}`}>
                    <td>
                      <Pill kind={flag.severity}>{flag.severity}</Pill>
                    </td>
                    <td>{flag.category}</td>
                    <td className="muted">{flag.condition || "—"}</td>
                    <td className="mono">{flag.subject || "—"}</td>
                    <td style={{ whiteSpace: "normal" }}>{flag.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="button-row">
        <button className="primary" onClick={acknowledge} disabled={busy}>
          {qc.acknowledged_at ? "Continue to curation" : "Acknowledge QC and continue"}
        </button>
        {busy && <Spinner />}
        {qc.acknowledged_at && (
          <span className="muted">
            Acknowledged by {qc.acknowledged_by || "an unnamed user"} on{" "}
            {dateTime(qc.acknowledged_at)}.
          </span>
        )}
      </div>
    </>
  );
}
